import { Injectable, EventEmitter } from '@angular/core';
import { SessionsService } from './sessions.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { urlApiLogin } from 'src/environments/environment';
import { Observable, of } from 'rxjs';
import { tap, mapTo, catchError } from 'rxjs/operators';
import { AuthTokenService } from './auth-token.service';
import { Router } from '@angular/router';
import { AlertifyService } from '../api-service/alertify.service';
import {SessionService} from '../core/services/session/session.service';

@Injectable()
export class AuthService {
  showLoginEmmiter = new EventEmitter<boolean>();
  showLoadingEmitter = new EventEmitter<boolean>();
  fechaAlert = new EventEmitter<object>();

  constructor(
    private router: Router,
    private sessions: SessionsService,
    private http: HttpClient,
    private authTokenService: AuthTokenService,
    private alertify: AlertifyService,
    private session: SessionService
  ) {}

  /**
   * Método que verifica se o usário está logado
   * @deprecated This method is deprecated and will be removed in future versions, use the isUserLoggedOnCognito() method instead.
   */
  isUserLogged() {
    const sLogin = this.sessions.getSessions(this.sessions.LOGIN);
    this.showLoginEmmiter.emit(true);
    if (sLogin) {
      this.showLoginEmmiter.emit(false);
      return true;
    }
    return false;
  }

  public isUserLoggedOnCognito(): boolean {
    return this.session.userIsLogged();
  }

  /**
   * Método que desloga o usuário
   * @deprecated This method is deprecated and will be removed in future versions, use the logoutUserFromCognito() method instead.
   */
  logoutUser() {
    this.showLoginEmmiter.emit(true);
    this.sessions.deleteSessions(this.sessions.LOGIN);
    this.sessions.deleteSessions(this.sessions.PERMISSOES);
  }

  goTop() {
    document.documentElement.scrollTop = 0;
  }

  /**
   * Método realiza o post com as credenciais do usuário para garantir acesso à aplicação.
   * @param credenciais E-mail e senha.
   * Retorna um boolean permitindo o acesso.
   */
  public login(credenciais): Observable<boolean> {
    return this.http.post<any>(`${urlApiLogin}/Login`, credenciais).pipe(
      tap((login) => this.guardaPermissoes(login)),
      mapTo(true),
      catchError(() => {
        return of(false);
      })
    );
  }

  /**
   * Método realiza o post com o refreshToken para finalizar o acesso à aplicação.
   */
  public logout() {
    const header = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http
      .post<any>(`${urlApiLogin}/Login/Logout`, JSON.stringify(this.getGuid()), { headers: header })
      .pipe(
        tap(() => {}),
        mapTo(true),
        catchError((error) => {
          this.alertify.error(error.error, 3);
          return of(false);
        })
      )
      .toPromise()
      .finally(() => {
        this.logoutUser();
        this.router.navigate(['login']);
      });
  }

  getRefreshToken() {
    return this.sessions.getSessions(this.sessions.LOGIN).tokenString;
  }

  private getGuid() {
    return this.sessions.getSessions(this.sessions.LOGIN).guid;
  }

  /**
   * Método guarda o token de acesso na sessão.
   * @param login Token retornado após o login.
   */
  private guardaPermissoes(login) {
    this.sessions.setSessions(this.sessions.LOGIN, login);
    this.authTokenService.atualizaPermissoes();
  }

  /**
   * Método verifica se o usuário possui token armazenado na sessão.
   */
  public usuarioLogado() {
    return !!this.sessions.getSessions(this.sessions.LOGIN);
  }

  /**
   * Método verifica se o horário atual é maior que o da expiração do token.
   */
  public acessoExpirado(): boolean {
    const expString = this.sessions.getSessions(this.sessions.LOGIN).expiration;
    if (expString) {
      const now = Date.now();
      const expDate = new Date(expString).getTime();
      const exp7200min = expDate + 7200 * 60 * 1000;
      if (now > exp7200min) {
        this.logout();
        return false;
      }
      if (now > expDate) return true;
    }
  }

  /**
   * Método faz o post do GUID na rota para atualizar o Token.
   */
  public async atualizaToken() {
    const header = new HttpHeaders().set('Content-Type', 'application/json');
    await this.http
      .post(`${urlApiLogin}/Login/Refresh`, JSON.stringify(this.getGuid()), { headers: header })
      .toPromise()
      .then((refresh) => {
        this.sessions.setSessions(this.sessions.LOGIN, refresh);
        this.authTokenService.atualizaPermissoes();
      });
  }

  /**
   * Método cria um GUID de recuperação de senha.
   * @param email
   */
  public getTokenRecuperacao(email: string) {
    const header = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.post(`${urlApiLogin}/Login/GetTokenRecuperacao`, JSON.stringify(email), { headers: header }).pipe(
      mapTo(true),
      catchError((error) => {
        console.error(error.error);
        return of(false);
      })
    );
  }

  public getTokenTrocaSenha(email: string) {
    const header = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.post(`${urlApiLogin}/Login/GetTokenTrocaSenha`, JSON.stringify(email), { headers: header }).pipe(
      mapTo(true),
      catchError((error) => {
        console.error(error.error);
        return of(false);
      })
    );
  }

  /**
   * Método envia o email, codigo gerado e a senha para alterar.
   * @param email
   * @param codigo
   * @param senha
   */
  public alteraSenha(email: string, codigo: string, senha: string) {
    const header = new HttpHeaders().set('Content-Type', 'application/json');
    const alterarSenha = {
      email,
      codigo,
      senha,
    };
    return this.http.post(`${urlApiLogin}/Login/AlteraSenha`, JSON.stringify(alterarSenha), { headers: header }).pipe(
      mapTo(true),
      catchError((error) => {
        console.error(error.error);
        return of(false);
      })
    );
  }
}
